import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faPhone, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Refs for focus trap
  const menuRef = useRef(null);
  const firstFocusableElementRef = useRef(null);
  const lastFocusableElementRef = useRef(null);

  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    setIsMenuOpen(newMenuState);
    
    // Use setTimeout to ensure state update has completed
    setTimeout(() => {
      if (newMenuState) {
        document.body.classList.add('menu-open');
      } else {
        document.body.classList.remove('menu-open');
      }
    }, 0);
  };

  // Effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine if we should collapse the navbar
      if (currentScrollY > 80) {
        setIsNavbarCollapsed(true);
      } else {
        setIsNavbarCollapsed(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);
  
  // Effect to clean up menu-open class when component unmounts
  useEffect(() => {
    return () => {
      // Clean up - remove menu-open class when component unmounts
      document.body.classList.remove('menu-open');
    };
  }, []);
  
  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMenuOpen) return;
    
    const menuElement = menuRef.current;
    if (!menuElement) return;
    
    // Get all focusable elements in the menu
    const focusableElements = menuElement.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    
    if (focusableElements.length === 0) return;
    
    // Set first and last focusable elements
    firstFocusableElementRef.current = focusableElements[0];
    lastFocusableElementRef.current = focusableElements[focusableElements.length - 1];
    
    // Focus the first element when menu opens
    firstFocusableElementRef.current.focus();
    
    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      
      // If shift + tab and focus is on first element, move to last element
      if (e.shiftKey && document.activeElement === firstFocusableElementRef.current) {
        e.preventDefault();
        lastFocusableElementRef.current.focus();
      }
      
      // If tab and focus is on last element, move to first element
      if (!e.shiftKey && document.activeElement === lastFocusableElementRef.current) {
        e.preventDefault();
        firstFocusableElementRef.current.focus();
      }
    };
    
    // Add event listener
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  return (
    <header className="site-header">
      
      <div className="header-announcement-bar-wrapper">
        <div className="header-border"></div>
        <div className="header-dropshadow"></div>
        
        <div className="header-background">
          <div className="header-background-solid"></div>
        </div>
        
        <div className={`header-inner ${isNavbarCollapsed ? 'collapsed' : ''}`}>
          <div className="header-background-blur"></div>
          
          {/* Desktop Header */}
          <div className="header-display-desktop">
            <div className="header-title-nav-wrapper">
              <div className="header-title">
                <div className="header-title-logo">
                  <Link to="/">
                    <img src="/logos/star-logo.png" alt="Star Deli Logo" />
                  </Link>
                </div>
              </div>
              
              <div className="header-nav">
                <div className="header-nav-wrapper">
                  <nav className="header-nav-list">
                    <div className="header-nav-item">
                      <NavLink to="/" end>Home</NavLink>
                    </div>
                    <div className="header-nav-item">
                      <NavLink to="/menu">Sandwiches</NavLink>
                    </div>
                    <div className="header-nav-item">
                      <NavLink to="/deli">Deli Menu</NavLink>
                    </div>
                    <div className="header-nav-item">
                      <NavLink to="/catering">Catering</NavLink>
                    </div>
                    <div className="header-nav-item">
                      <NavLink to="/about">About Us</NavLink>
                    </div>
                  </nav>
                </div>
              </div>
            </div>
            
            <div className="header-actions">
              <div className="header-actions-action">
                <a href="tel:248-352-7377" className="btn primary">
                  <FontAwesomeIcon icon={faPhone} /> Order Now
                </a>
              </div>
            </div>
            
            {/* Burger Menu Button */}
            <div className="header-burger">
              <button
                className="header-burger-btn"
                onClick={toggleMenu}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                <div className="burger-box">
                  <div className={`burger-inner ${isMenuOpen ? 'open' : ''}`}>
                    <div className="top-bun"></div>
                    <div className="patty"></div>
                    <div className="bottom-bun"></div>
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          {/* Mobile Header */}
          <div className="header-display-mobile">
            <div className="header-title-nav-wrapper">
              <div className="header-title">
                <div className="header-title-logo">
                  <Link to="/">
                    <img src="/logos/star-logo.png" alt="Star Deli Logo" />
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="header-actions">
              <div className="header-actions-action">
                <a href="tel:248-352-7377" className="btn primary mobile-order-btn">
                  Order
                </a>
              </div>
            </div>
            
            {/* Burger Menu Button */}
            <div className="header-burger">
              <button
                className="header-burger-btn"
                onClick={toggleMenu}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                <div className="burger-box">
                  <div className={`burger-inner ${isMenuOpen ? 'open' : ''}`}>
                    <div className="top-bun"></div>
                    <div className="patty"></div>
                    <div className="bottom-bun"></div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        className={`menu-overlay ${isMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal={isMenuOpen}
        aria-label="Main Navigation"
        ref={menuRef}
      >
        <div className="menu-overlay-inner">
          <div className="menu-overlay-content">
            <div className="mobile-nav-links">
              <NavLink to="/" end onClick={toggleMenu}>
                Home <FontAwesomeIcon icon={faAngleRight} />
              </NavLink>
              <NavLink to="/menu" onClick={toggleMenu}>
                Sandwiches <FontAwesomeIcon icon={faAngleRight} />
              </NavLink>
              <NavLink to="/deli" onClick={toggleMenu}>
                Deli Menu <FontAwesomeIcon icon={faAngleRight} />
              </NavLink>
              <NavLink to="/catering" onClick={toggleMenu}>
                Catering <FontAwesomeIcon icon={faAngleRight} />
              </NavLink>
              <NavLink to="/about" onClick={toggleMenu}>
                About Us <FontAwesomeIcon icon={faAngleRight} />
              </NavLink>
              {/* Contact information is in the footer and mobile contact section below */}
            </div>
            
            <div className="mobile-contact-info">
              <div className="mobile-phone">
                <h4>Order by Phone</h4>
                <a href="tel:248-352-7377">248-352-7377</a>
              </div>
              
              <div className="mobile-social">
                <h4>Connect With Us</h4>
                <div className="social-icons">
                  <a href="https://facebook.com" aria-label="Facebook" rel="noopener noreferrer" target="_blank">
                    <FontAwesomeIcon icon={faFacebook} />
                  </a>
                  <a href="https://instagram.com" aria-label="Instagram" rel="noopener noreferrer" target="_blank">
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                  <a href="https://twitter.com" aria-label="Twitter" rel="noopener noreferrer" target="_blank">
                    <FontAwesomeIcon icon={faTwitter} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;